import React from "react";
import { useTheme } from "../../Context/ThemeContext";
import "./PremiumLoading.css";

function hexToRgbString(hex, fallback) {
  if (!hex || typeof hex !== "string") return fallback;
  let normalized = hex.trim().replace("#", "");
  if (normalized.length === 3) {
    normalized = normalized.split("").map((char) => char + char).join("");
  }
  if (!/^[a-fA-F0-9]{6}$/.test(normalized)) return fallback;

  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

export function PremiumLoader({
  title = "Loading data",
  subtitle = "Preparing your workspace...",
  compact = false,
  className = "",
  tone = "default",
}) {
  const { brandColors, isDarkMode } = useTheme();
  const primary = brandColors?.primary || "#3b82f6";
  const secondary = brandColors?.secondary || "#1e293b";
  const primaryRgb = hexToRgbString(primary, "59, 130, 246");
  const secondaryRgb = hexToRgbString(secondary, "30, 41, 59");
  const themeVars = {
    "--premium-accent": primary,
    "--premium-accent-2": secondary,
    "--premium-accent-soft": `rgba(${primaryRgb}, 0.16)`,
    "--premium-accent-shadow": `rgba(${primaryRgb}, 0.35)`,
    "--premium-title": primary,
    "--premium-shimmer-a": `rgba(${primaryRgb}, 0.08)`,
    "--premium-shimmer-b": `rgba(${secondaryRgb}, 0.04)`,
  };
  const containerClass = compact
    ? "premium-loading-root p-6"
    : "premium-loading-root min-h-[260px] p-6 md:p-10 flex items-center justify-center";

  return (
    <div
      className={`${containerClass} premium-tone-${tone} ${isDarkMode ? "premium-dark" : ""} ${className}`}
      style={themeVars}
    >
      <div className="premium-loader-shell w-full max-w-[680px] p-6 md:p-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="premium-loader-dot"></span>
          <p className="premium-loader-title m-0 text-[11px] font-black uppercase tracking-[0.22em]">{title}</p>
        </div>
        <p className="premium-loader-subtitle m-0 text-sm md:text-base font-semibold">{subtitle}</p>
        <div className="premium-loader-bar mt-5"></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <div className="premium-shimmer h-10"></div>
          <div className="premium-shimmer h-10"></div>
          <div className="premium-shimmer h-10"></div>
          <div className="premium-shimmer h-10"></div>
        </div>
      </div>
    </div>
  );
}

export function PremiumCardSkeleton({ lines = 3, className = "" }) {
  const { brandColors, isDarkMode } = useTheme();
  const primary = brandColors?.primary || "#3b82f6";
  const secondary = brandColors?.secondary || "#1e293b";
  const primaryRgb = hexToRgbString(primary, "59, 130, 246");
  const secondaryRgb = hexToRgbString(secondary, "30, 41, 59");
  return (
    <div
      className={`premium-loader-shell p-5 ${isDarkMode ? "premium-dark" : ""} ${className}`}
      style={{
        "--premium-accent": primary,
        "--premium-accent-2": secondary,
        "--premium-accent-soft": `rgba(${primaryRgb}, 0.16)`,
        "--premium-accent-shadow": `rgba(${primaryRgb}, 0.35)`,
        "--premium-title": primary,
        "--premium-shimmer-a": `rgba(${primaryRgb}, 0.08)`,
        "--premium-shimmer-b": `rgba(${secondaryRgb}, 0.04)`,
      }}
    >
      <div className="premium-shimmer h-8 w-8 rounded-xl mb-4"></div>
      {Array.from({ length: lines }).map((_, idx) => (
        <div key={idx} className={`premium-shimmer h-4 mb-3 ${idx === lines - 1 ? "w-2/3" : "w-full"}`}></div>
      ))}
    </div>
  );
}

export function PremiumSuspenseFallback({ label = "Loading section..." }) {
  const { brandColors, isDarkMode } = useTheme();
  const primary = brandColors?.primary || "#3b82f6";
  const secondary = brandColors?.secondary || "#1e293b";
  const primaryRgb = hexToRgbString(primary, "59, 130, 246");
  const secondaryRgb = hexToRgbString(secondary, "30, 41, 59");
  return (
    <div
      className={`premium-loading-root min-h-[240px] flex flex-col items-center justify-center gap-4 p-6 ${isDarkMode ? "premium-dark" : ""}`}
      style={{
        "--premium-accent": primary,
        "--premium-accent-2": secondary,
        "--premium-accent-soft": `rgba(${primaryRgb}, 0.16)`,
        "--premium-accent-shadow": `rgba(${primaryRgb}, 0.35)`,
        "--premium-title": primary,
        "--premium-shimmer-a": `rgba(${primaryRgb}, 0.08)`,
        "--premium-shimmer-b": `rgba(${secondaryRgb}, 0.04)`,
      }}
    >
      <div className="premium-loader-dot"></div>
      <p className="premium-loader-subtitle m-0 text-xs font-black uppercase tracking-[0.2em]">{label}</p>
      <div className="w-full max-w-[340px] premium-loader-bar"></div>
    </div>
  );
}
